import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { KeyRound, Copy, Check, Search, User, Loader2, UserPlus, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const AgentCredentialsAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmUser, setConfirmUser] = useState(null);
  const [emailToAgent, setEmailToAgent] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Add-agent modal state
  const [addOpen, setAddOpen] = useState(false);
  const [newAgent, setNewAgent] = useState({ full_name: '', email: '', phone: '', role: 'agent', email_to_agent: true });
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);

  // Seed-prod state
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedCopied, setSeedCopied] = useState(false);

  const runSeed = async () => {
    if (!window.confirm(
      "Run the one-time Production Seed?\n\n" +
      "This creates: Benjamin (admin), Yolanda, Sharon, Telesa (agents), Monique's agent website, and the Property Management routing rule.\n\n" +
      "Idempotent — safe to re-run. Existing users will NOT have their passwords reset."
    )) return;
    setSeedRunning(true);
    setSeedResult(null);
    try {
      const res = await api.post('/admin/seed-prod');
      setSeedResult(res.data);
      if (res.data?.ok) {
        toast.success('Seed complete — review the output below');
        load(); // refresh users list
      } else {
        toast.error(`Seed exited with code ${res.data?.returncode}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed to run');
    } finally {
      setSeedRunning(false);
    }
  };

  const copySeedOutput = async () => {
    if (!seedResult?.output) return;
    try {
      await navigator.clipboard.writeText(seedResult.output);
      setSeedCopied(true);
      setTimeout(() => setSeedCopied(false), 2000);
    } catch {}
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (!confirmUser) return;
    setResetting(true);
    try {
      const res = await api.post(`/admin/users/${confirmUser.id}/reset-password?email_to_agent=${emailToAgent}`);
      setResult(res.data);
      toast.success(`Password reset for ${confirmUser.full_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const copyPw = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const close = () => {
    setConfirmUser(null);
    setResult(null);
    setEmailToAgent(true);
    setCopied(false);
  };

  const createAgent = async () => {
    if (!newAgent.full_name.trim()) return toast.error('Full name required');
    if (!newAgent.email.trim() || !newAgent.email.includes('@')) return toast.error('Valid email required');
    setCreating(true);
    try {
      const res = await api.post('/admin/users', newAgent);
      setCreatedResult(res.data);
      toast.success(`Account created for ${res.data.user.full_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const closeAdd = () => {
    setAddOpen(false);
    setNewAgent({ full_name: '', email: '', phone: '', role: 'agent', email_to_agent: true });
    setCreatedResult(null);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.full_name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('User deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q);
  });

  return (
    <Card data-testid="agent-credentials-admin">
      <CardHeader>
        <div className="flex justify-between items-start gap-3">
          <div>
            <CardTitle>Agent Credentials & Password Resets</CardTitle>
            <CardDescription>
              Add new agents or reset an existing agent's password. They can log in with the temp password, then change it in the portal.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runSeed}
              disabled={seedRunning}
              variant="outline"
              className="border-[#BCA480] text-[#434345] hover:bg-[#faf8f4]"
              data-testid="run-seed-prod-btn"
              title="One-time setup: seeds Benjamin + agents + Monique + PM routing rule"
            >
              {seedRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {seedRunning ? 'Running…' : 'Run Prod Seed'}
            </Button>
            <Button onClick={() => setAddOpen(true)} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="add-agent-btn">
              <UserPlus className="w-4 h-4 mr-2" />Add Agent
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {seedResult && (
          <div className="mb-4 bg-[#1b1b1d] text-[#e6dec7] rounded-lg p-4 relative" data-testid="seed-result-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-[#BCA480] font-semibold">
                Seed output {seedResult.ok ? '· success' : `· exit ${seedResult.returncode}`}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={copySeedOutput} className="h-7 text-[#BCA480] hover:bg-white/10" data-testid="seed-copy-btn">
                  {seedCopied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                  {seedCopied ? 'Copied' : 'Copy'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSeedResult(null)} className="h-7 text-[#BCA480] hover:bg-white/10" data-testid="seed-close-btn">
                  Close
                </Button>
              </div>
            </div>
            <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono max-h-[400px] overflow-y-auto">
{seedResult.output || '(no output)'}
            </pre>
            {seedResult.stderr && (
              <details className="mt-2 text-[10px] opacity-70">
                <summary className="cursor-pointer">stderr (warnings)</summary>
                <pre className="whitespace-pre-wrap mt-1">{seedResult.stderr}</pre>
              </details>
            )}
            <div className="mt-3 text-[11px] text-amber-300 italic">
              ⚠️ Temp passwords above are one-time. Share with each agent securely (text/call — not email). Users reset on first login.
            </div>
          </div>
        )}

        <div className="mb-4 flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search agents by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="user-search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading agents…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No agents match your search.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(u => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded"
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <User className="w-8 h-8 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.full_name || <em className="text-gray-400">no name</em>}</div>
                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  </div>
                </div>
                <Badge className={`${u.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-blue-100 text-blue-800 border-blue-300'} border capitalize`}>
                  {u.role}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmUser(u)}
                  data-testid={`reset-btn-${u.id}`}
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1" />
                  Reset Password
                </Button>
                {u.role !== 'admin' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteUser(u)}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={(v) => !v && closeAdd()}>
        <DialogContent data-testid="add-agent-dialog">
          {!createdResult ? (
            <>
              <DialogHeader>
                <DialogTitle>Add New Agent</DialogTitle>
                <DialogDescription>Create an Agent Portal account. A temp password is generated automatically.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={newAgent.full_name} onChange={(e) => setNewAgent(a => ({ ...a, full_name: e.target.value }))} placeholder="Monique Smith" data-testid="new-agent-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={newAgent.email} onChange={(e) => setNewAgent(a => ({ ...a, email: e.target.value }))} placeholder="monique@bespokerealty.net" data-testid="new-agent-email" />
                </div>
                <div>
                  <Label>Phone (optional)</Label>
                  <Input value={newAgent.phone} onChange={(e) => setNewAgent(a => ({ ...a, phone: e.target.value }))} placeholder="(901) 555-0000" data-testid="new-agent-phone" />
                </div>
                <div>
                  <Label>Role</Label>
                  <select
                    className="w-full h-10 border border-gray-200 rounded px-3 text-sm"
                    value={newAgent.role}
                    onChange={(e) => setNewAgent(a => ({ ...a, role: e.target.value }))}
                    data-testid="new-agent-role"
                  >
                    <option value="agent">Agent</option>
                    <option value="admin">Admin / Broker</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newAgent.email_to_agent}
                    onChange={(e) => setNewAgent(a => ({ ...a, email_to_agent: e.target.checked }))}
                    data-testid="new-agent-email-checkbox"
                  />
                  <span>Email the temp password to the agent</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAdd} disabled={creating}>Cancel</Button>
                <Button onClick={createAgent} disabled={creating} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="create-agent-btn">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</> : 'Create Account'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>✅ Account created for {createdResult.user.full_name}</DialogTitle>
                <DialogDescription>
                  {createdResult.email_sent
                    ? `A welcome email with the temp password was sent to ${createdResult.user.email}.`
                    : `Copy the temp password and share it with the agent (email was skipped).`}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-2">
                <div className="text-xs text-gray-600">Email: <strong className="text-gray-900">{createdResult.user.email}</strong></div>
                <div className="bg-[#faf8f4] border-2 border-dashed border-[#BCA480] rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">Temporary password</div>
                  <div className="font-mono text-2xl tracking-widest font-bold text-[#434345]" data-testid="new-agent-temp-password">
                    {createdResult.temp_password}
                  </div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(createdResult.temp_password); toast.success('Copied'); }}>
                  <Copy className="w-4 h-4 mr-2" />Copy password
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={closeAdd} className="bg-[#BCA480] hover:bg-[#a89470] text-white">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmUser} onOpenChange={(v) => !v && close()}>
        <DialogContent data-testid="reset-dialog">
          {!result ? (
            <>
              <DialogHeader>
                <DialogTitle>Reset password for {confirmUser?.full_name || confirmUser?.email}?</DialogTitle>
                <DialogDescription>
                  A new temporary password will be generated. The agent uses it to log in, then changes it in Profile.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={emailToAgent}
                    onChange={(e) => setEmailToAgent(e.target.checked)}
                    data-testid="email-agent-checkbox"
                  />
                  <span>Also email the temporary password to <strong>{confirmUser?.email}</strong></span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={resetting}>Cancel</Button>
                <Button onClick={doReset} disabled={resetting} className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="confirm-reset-btn">
                  {resetting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting…</> : 'Reset Password'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>✅ Password reset for {result.full_name}</DialogTitle>
                <DialogDescription>
                  {result.email_sent
                    ? `Temp password was emailed to ${result.email}.`
                    : `Copy and share this temp password with the agent (email was skipped).`}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-[#faf8f4] border-2 border-dashed border-[#BCA480] rounded-lg p-4 text-center">
                  <div className="text-xs text-gray-600 mb-1">Temporary password</div>
                  <div className="font-mono text-2xl tracking-widest font-bold text-[#434345]" data-testid="temp-password-display">
                    {result.temp_password}
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-3"
                  onClick={copyPw}
                  data-testid="copy-password-btn"
                >
                  {copied ? <><Check className="w-4 h-4 mr-2 text-emerald-600" />Copied!</> : <><Copy className="w-4 h-4 mr-2" />Copy password</>}
                </Button>
                <p className="text-xs text-gray-500 mt-3">
                  Tell the agent to log in at bespokerealty.net/agent-portal with this password, then change it in their Profile.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={close} className="bg-[#BCA480] hover:bg-[#a89470] text-white">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AgentCredentialsAdmin;
